import { Injectable } from "@angular/core";
import { AccountType } from "../../types/AccountType";
import { AccountOwnerType } from "../../types/AccountOwnerType";

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface MezFieldValidation {
  isValid: boolean;
  errorMessage: string;
}

export interface FormFieldValidation {
  validAccountNameInput: boolean;
  validBalanceInput: boolean;
  validSubAccountTypeInput: boolean;
  validMortgageInput: boolean;
}

const SAFE_STRING_CREDITOR_PATTERN = /^[a-zA-Z0-9 \-()/]+$/;
const TEXT_ASSET_LIABILITY_DENY_PATTERN = /[_~`!@#$^*+={}[\]|\\:;"?]/;

const SAFE_STRING_MAX_LENGTH = 100;
const TEXT_MAX_LENGTH = 500;

export interface AccountOwnerValidationResult {
  isValid: boolean;
  hasError: boolean;
  errorMessage?: string;
}

@Injectable({
  providedIn: "root",
})
export class FormValidationService {
  /**
   * Validates the owner input field
   */
  validateOwnerInput(accountOwner: string): boolean {
    return !!accountOwner;
  }

  /**
   * Validates the account name input field
   */
  validateAccountNameInput(accountName: string): boolean {
    return !!accountName;
  }

  /**
   * Validates the custom institution name field (shown when "Other" is selected)
   */
  validateInstitutionNameInput(name: string): boolean {
    return !!name?.trim();
  }

  /**
   * Validates the balance input field
   * Ensures the balance is within 32-bit signed integer range (-2,147,483,648 to 2,147,483,647)
   */
  validateBalanceInput(balance: number | string | null): boolean {
    if (!balance) return false;

    const sanitized = this.sanitizeCurrencyInput(balance);

    // Check if the string representation is too long before parsing
    // Max 32-bit signed int is 2147483647 (10 digits), so check length first
    const digitsOnly = sanitized.replace(/[^0-9]/g, "");
    if (digitsOnly.length > 10) {
      return false; // Too many digits, definitely out of range
    }

    const parsed = parseInt(sanitized, 10);

    // Check if it's a valid number and within 32-bit signed integer range
    if (isNaN(parsed)) return false;

    // 32-bit signed integer range: -2^31 to 2^31-1
    const MIN_32BIT_INT = -2147483648;
    const MAX_32BIT_INT = 2147483647;

    return parsed >= MIN_32BIT_INT && parsed <= MAX_32BIT_INT;
  }

  /**
   * Validates the sub account type input field
   */
  validateSubAccountTypeInput(subAccountType: string, showSubAccountType: boolean): boolean {
    if (!showSubAccountType) {
      return true;
    }
    return !!subAccountType;
  }

  /**
   * Validates the mortgage amount input field for real estate accounts
   */
  validateMortgageInput(mortgageAmount: number | null): boolean {
    if (!mortgageAmount) {
      return true; // Mortgage amount is optional
    }

    const sanitizedMortgageAmount = this.sanitizeCurrencyInput(mortgageAmount);
    return !isNaN(Number(sanitizedMortgageAmount));
  }

  /**
   * Sanitizes currency input by removing $ and commas
   */
  sanitizeCurrencyInput(input: number | string | null): string {
    if (!input) return "";
    return String(input).replace(/[\$,]/g, "");
  }

  /**
   * Validates all form inputs based on account type
   */
  validateAllInputs(
    accountName: string,
    balance: number | string | null,
    subAccountType: string,
    mortgageAmount: number | null,
    accountType: AccountType,
    showSubAccountType: boolean,
  ): FormFieldValidation {
    const validation: FormFieldValidation = {
      validAccountNameInput: this.validateAccountNameInput(accountName),
      validBalanceInput: this.validateBalanceInput(balance),
      validSubAccountTypeInput: this.validateSubAccountTypeInput(
        subAccountType,
        showSubAccountType,
      ),
      validMortgageInput: true,
    };

    // Special validation for real estate accounts
    if (accountType === AccountType.REAL_ESTATE) {
      validation.validMortgageInput = this.validateMortgageInput(mortgageAmount);
    }

    // Trust accounts have no subAccountType dropdown, so skip that validation
    if (accountType === AccountType.TRUST) {
      validation.validSubAccountTypeInput = true;
    }

    // Special handling for account types without sub account types
    if (!showSubAccountType && this.isAccountTypeWithoutSubType(accountType)) {
      validation.validSubAccountTypeInput = true;
    }

    return validation;
  }

  /**
   * Checks if the account type doesn't require sub account type selection
   */
  private isAccountTypeWithoutSubType(accountType: AccountType): boolean {
    return (
      accountType === AccountType.BANK ||
      accountType === AccountType.REAL_ESTATE ||
      accountType === AccountType.LIABILITIES
    );
  }

  /**
   * Checks if there are any validation errors
   */
  hasValidationErrors(validation: FormFieldValidation, accountType: AccountType): boolean {
    const baseValidation =
      validation.validAccountNameInput &&
      validation.validBalanceInput &&
      validation.validSubAccountTypeInput;

    // Include mortgage validation for real estate accounts
    if (accountType === AccountType.REAL_ESTATE) {
      return !(baseValidation && validation.validMortgageInput);
    }

    return !baseValidation;
  }

  /**
   * Gets validation error messages for failed validations
   */
  getValidationErrorMessages(validation: FormFieldValidation): string[] {
    const errors: string[] = [];

    if (!validation.validAccountNameInput) {
      errors.push("Account name is required");
    }
    if (!validation.validBalanceInput) {
      errors.push("Please enter a valid balance (maximum value: $2,147,483,647)");
    }
    if (!validation.validSubAccountTypeInput) {
      errors.push("Account type is required");
    }
    if (!validation.validMortgageInput) {
      errors.push("Please enter a valid mortgage amount");
    }

    return errors;
  }

  /**
   * Comprehensive validation result with error messages
   */
  getValidationResult(
    accountName: string,
    balance: number | string | null,
    subAccountType: string,
    mortgageAmount: number | null,
    accountType: AccountType,
    showSubAccountType: boolean,
  ): ValidationResult {
    const validation = this.validateAllInputs(
      accountName,
      balance,
      subAccountType,
      mortgageAmount,
      accountType,
      showSubAccountType,
    );

    const hasErrors = this.hasValidationErrors(validation, accountType);
    const errors = hasErrors ? this.getValidationErrorMessages(validation) : [];

    return {
      isValid: !hasErrors,
      errors,
    };
  }

  // Account Owner Validation Methods

  /**
   * Validates if an account owner type is selected
   */
  validateAccountOwnerSelection(selectedAccountOwnerType: AccountOwnerType | undefined): boolean {
    return selectedAccountOwnerType !== undefined && selectedAccountOwnerType !== null;
  }

  /**
   * Validates account owner type is valid for the given account type
   */
  isValidAccountOwnerTypeForAccount(
    selectedAccountOwnerType: AccountOwnerType | undefined,
    accountType: AccountType,
  ): boolean {
    if (!this.validateAccountOwnerSelection(selectedAccountOwnerType)) {
      return false;
    }

    // Special validation for retirement accounts
    if (accountType === AccountType.RETIREMENT_ANNUITY) {
      return selectedAccountOwnerType === AccountOwnerType.INDIVIDUAL_BY_NAME;
    }

    // For other account types, all available owner types are valid
    return true;
  }

  /**
   * Validates account owner selection and provides detailed error information
   */
  validateAccountOwner(
    selectedAccountOwnerType: AccountOwnerType | undefined,
    accountType: AccountType,
  ): AccountOwnerValidationResult {
    if (!this.validateAccountOwnerSelection(selectedAccountOwnerType)) {
      return {
        isValid: false,
        hasError: true,
        errorMessage: "Please select an account owner type",
      };
    }

    if (!this.isValidAccountOwnerTypeForAccount(selectedAccountOwnerType, accountType)) {
      return {
        isValid: false,
        hasError: true,
        errorMessage: "The selected account owner type is not valid for this account type",
      };
    }

    return {
      isValid: true,
      hasError: false,
    };
  }

  /**
   * Gets available account owner types for a given account type
   */
  getAvailableAccountOwnerTypes(accountType: AccountType): AccountOwnerType[] {
    if (accountType === AccountType.RETIREMENT_ANNUITY) {
      // Only individual by name is allowed for retirement accounts
      return [AccountOwnerType.INDIVIDUAL_BY_NAME];
    }

    // For other account types, return all available types
    return [AccountOwnerType.INDIVIDUAL_BY_NAME, AccountOwnerType.TRUST];
  }

  /**
   * Determines if account owner selection should show error state
   */
  shouldShowAccountOwnerError(
    selectedAccountOwnerType: AccountOwnerType | undefined,
    accountType: AccountType,
    hasSubmitted: boolean = false,
  ): boolean {
    if (!hasSubmitted) {
      return false;
    }

    const validation = this.validateAccountOwner(selectedAccountOwnerType, accountType);
    return validation.hasError;
  }

  // MEZ Input Validation

  private mezValid(): MezFieldValidation {
    return { isValid: true, errorMessage: "" };
  }

  validateMezAssetLiabilityName(value: string): MezFieldValidation {
    if (!value) return this.mezValid();

    if (value.length > TEXT_MAX_LENGTH) {
      return {
        isValid: false,
        errorMessage: `Name cannot exceed ${TEXT_MAX_LENGTH} characters`,
      };
    }

    const match = TEXT_ASSET_LIABILITY_DENY_PATTERN.exec(value);
    if (match) {
      return {
        isValid: false,
        errorMessage: `Name contains a restricted character: ${match[0]}`,
      };
    }

    return this.mezValid();
  }

  validateMezCreditor(value: string): MezFieldValidation {
    if (!value) return this.mezValid();

    if (value.length > SAFE_STRING_MAX_LENGTH) {
      return {
        isValid: false,
        errorMessage: `Creditor cannot exceed ${SAFE_STRING_MAX_LENGTH} characters`,
      };
    }

    if (!SAFE_STRING_CREDITOR_PATTERN.test(value)) {
      return {
        isValid: false,
        errorMessage:
          "Only letters, numbers, spaces, hyphens, parentheses, and forward slashes are allowed",
      };
    }

    return this.mezValid();
  }
}
